<script setup lang="ts">
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';

import UiField from './UiField.vue';

interface Props {
  label?: string;
  /**
   * Appends the required marker to the label and sets `aria-required`.
   */
  required?: boolean;
  /**
   * Hint under the field. Superseded by `error` while the field is invalid.
   */
  helper?: string;
  /**
   * Validation message. Its presence also marks the field invalid.
   */
  error?: string;
  /**
   * Marks the field invalid without its own message, for group-level errors.
   */
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Omit for the normal size.
   */
  size?: 'small' | 'large';
  variant?: 'outlined' | 'filled';
  /**
   * Overrides the generated input id when a caller has to address the field.
   */
  inputId?: string;
}

// `class` and `style` size the field, so they stay on the root; every other
// attribute (name, type, autocomplete, maxlength, listeners) reaches the input.
defineOptions({ inheritAttrs: false });

defineProps<Props>();

// `v-model.trim` reaches a component as a modifier the child must honour;
// without this the modifier is silently dropped.
const [model, modelModifiers] = defineModel<string, 'trim'>({
  set: (value) => (modelModifiers.trim ? value?.trim() : value),
});
</script>

<template>
  <UiField
    :class="['ui-input-text', $attrs.class, { 'ui-input-text--disabled': disabled }]"
    :style="$attrs.style"
    :label="label"
    :required="required"
    :helper="helper"
    :error="error"
    :invalid="invalid"
    :inputId="inputId"
    v-slot="field"
  >
    <IconField class="ui-input-text__field">
      <InputIcon v-if="$slots.leftIcon" class="ui-input-text__icon">
        <slot name="leftIcon" />
      </InputIcon>

      <InputText
        v-bind="{ ...$attrs, class: undefined, style: undefined }"
        :id="field.inputId"
        v-model="model"
        class="ui-input-text__input"
        :placeholder="placeholder"
        :disabled="disabled"
        :invalid="field.invalid"
        :size="size"
        :variant="variant"
        :aria-required="required || undefined"
        :aria-describedby="field.describedBy"
      />

      <InputIcon v-if="$slots.rightIcon" class="ui-input-text__icon">
        <slot name="rightIcon" />
      </InputIcon>
    </IconField>
  </UiField>
</template>

<style scoped lang="scss">
.ui-input-text {
  &__field {
    width: 100%;
  }

  &__input {
    width: 100%;

    // PrimeVue insets the icon by the form-field padding and pads the input by
    // twice it; Figma insets by $spacing-sm and gaps icon to text by $spacing-s.
    &:not(:first-child) {
      padding-inline-start: calc(#{$spacing-sm} + #{$icon-size-sm} + #{$spacing-s});
    }

    &:not(:last-child) {
      padding-inline-end: calc(#{$spacing-sm} + #{$icon-size-sm} + #{$spacing-s});
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: $icon-size-sm;
    height: $icon-size-sm;
    font-size: $icon-size-sm;
    color: $color-icon-muted;

    &:first-child {
      inset-inline-start: $spacing-sm;
    }

    &:last-child {
      inset-inline-end: $spacing-sm;
    }

    :deep(svg) {
      width: 100%;
      height: 100%;
    }
  }

  &--disabled &__icon {
    color: $color-icon-disabled;
  }
}
</style>
